
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import axios from '@/plugins/axios';
import errorHandler from '@/services/errorHandler';

const STORAGE_KEY_FOREVER = 'tax_notification_hidden_forever';
const STORAGE_KEY_UNTIL = 'tax_notification_hidden_until';
const HIDE_HOURS = 2;
const HIDE_DAYS = 3;

export default defineComponent({
  name: 'TaxSettingsNotification',
  data() {
    return {
      visible: false,
      taxSettingsNeeded: false,
    };
  },
  computed: {
    ...mapGetters(['company']),
    storageKeyForever(): string {
      return `${STORAGE_KEY_FOREVER}_${this.company?.id || 0}`;
    },
    storageKeyUntil(): string {
      return `${STORAGE_KEY_UNTIL}_${this.company?.id || 0}`;
    },
  },
  watch: {
    company: {
      handler() {
        this.fetchAndCheck();
      },
      deep: true,
    },
    '$route.path'() {
      this.fetchAndCheck();
    },
  },
  mounted() {
    this.fetchAndCheck();
  },
  methods: {
    async fetchAndCheck() {
      if (!this.company?.id) {
        this.visible = false;
        return;
      }
      try {
        const result = await axios.get('settings') as { settings?: { tax_system?: string; vat_rate?: string } };
        const s = result?.settings;
        const hasValue = (v: unknown) => v != null && String(v).trim() !== '';
        this.taxSettingsNeeded = !(hasValue(s?.tax_system) && hasValue(s?.vat_rate));
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
        this.taxSettingsNeeded = false;
      }
      this.checkShouldShow();
    },
    checkShouldShow() {
      if (!this.taxSettingsNeeded) {
        this.visible = false;
        return;
      }
      if (this.$route.path?.startsWith?.('/settings')) {
        this.visible = false;
        return;
      }
      if (localStorage.getItem(this.storageKeyForever) === '1') {
        this.visible = false;
        return;
      }
      const until = localStorage.getItem(this.storageKeyUntil);
      if (until) {
        const ts = parseInt(until, 10);
        if (ts && Date.now() < ts) {
          this.visible = false;
          return;
        }
      }
      this.visible = true;
    },
    goToSettings() {
      this.suppressFor2Hours();
      this.visible = false;
      this.$router.push('/settings?tab=entity');
    },
    suppressFor2Hours() {
      const until = Date.now() + HIDE_HOURS * 60 * 60 * 1000;
      localStorage.setItem(this.storageKeyUntil, String(until));
    },
    hideFor3Days() {
      const until = Date.now() + HIDE_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(this.storageKeyUntil, String(until));
      this.visible = false;
    },
    hideForever() {
      localStorage.setItem(this.storageKeyForever, '1');
      this.visible = false;
    },
  },
});
